/**
 * Builds yearly tables from the same pipeline as MonthlySaleReportPage (per month).
 * Rows follow the financial year April → March (`year` = FY start calendar year).
 */
import {
  MONTHLY_AUTOS_BRANDS,
  fetchMonthlyAutosReport,
  mapMonthlyAutosReportToSalePageData,
} from "./monthlyAutosReportService";
import {
  formatFinancialYearLabel,
  formatMonthShortYear,
  getFinancialYearMonthSequence,
  FY_MONTH_ORDER_SHORT,
} from "../utils/financialYear";
import {
  loadLatestTopSales,
  loadMonthlyManualEntries,
  mergeCurrentMonthOverridesIntoSalePageData,
  mergeMonthlyManualIntoReport,
  mergePreviousMonthManualIntoSalePageData,
} from "./monthlySaleReportManualService";

/**
 * One month of data in the same shape as MonthlySaleReportPage `data` after merges.
 * @param {{ importOnly?: boolean, skipPrecalc?: boolean }} [opts]
 *   When `importOnly` is true, uses `sales_import_months` (same as Monthly sale report import).
 *   Pass `skipPrecalc: true` to aggregate live lineChunks (needed for partial weekly uploads).
 */
export async function loadMonthSalePageData(
  year,
  monthIndex,
  branchFilter = "Main",
  { importOnly = false, skipPrecalc = false } = {}
) {
  if (importOnly) {
    const report = await fetchMonthlyAutosReport(year, monthIndex, branchFilter, {
      importOnly: true,
      dateScope: "full",
      skipPrecalc,
    });
    return mapMonthlyAutosReportToSalePageData(report);
  }

  const prevPeriod = new Date(year, monthIndex - 1, 1);
  const [report, manualBundle, prevManualBundle, fixedTopSales] = await Promise.all([
    fetchMonthlyAutosReport(year, monthIndex, branchFilter),
    loadMonthlyManualEntries(year, monthIndex, branchFilter),
    loadMonthlyManualEntries(prevPeriod.getFullYear(), prevPeriod.getMonth(), branchFilter),
    loadLatestTopSales(year, monthIndex, 120, branchFilter),
  ]);
  const merged = mergeMonthlyManualIntoReport(report, manualBundle, branchFilter);
  const mapped = mapMonthlyAutosReportToSalePageData(merged);
  const mappedWithPrevManual = mergePreviousMonthManualIntoSalePageData(
    mapped,
    prevManualBundle,
    branchFilter
  );
  return mergeCurrentMonthOverridesIntoSalePageData(
    mappedWithPrevManual,
    manualBundle,
    prevManualBundle,
    fixedTopSales,
    branchFilter
  );
}

/**
 * @returns {Promise<Array<{ fySlot: number, monthIndex: number, calendarYear: number, monthLabel: string, data: object }>>}
 * Loads four FY months at a time (April → March).
 * @param {number} fyStartYear Calendar year in which FY begins (April 1).
 */
export async function loadYearlySaleReportRows(fyStartYear, branchFilter = "Main") {
  const seq = getFinancialYearMonthSequence(fyStartYear);
  const out = [];
  for (let i = 0; i < 12; i += 4) {
    const slice = seq.slice(i, i + 4);
    const chunk = await Promise.all(
      slice.map(({ calendarYear, monthIndex }) =>
        loadMonthSalePageData(calendarYear, monthIndex, branchFilter)
      )
    );
    slice.forEach((spec, j) => {
      out.push({
        ...spec,
        data: chunk[j],
        monthLabel: formatMonthShortYear(spec.calendarYear, spec.monthIndex),
      });
    });
  }
  out.sort((a, b) => a.fySlot - b.fySlot);
  return out;
}

export function sumYearlyNumeric(rows, key) {
  return rows.reduce((s, r) => s + (Number(r.data?.[key]) || 0), 0);
}

/**
 * YTD totals for key metrics shown in the yearly grid.
 */
export function computeYearlyTotals(rows) {
  const keys = [
    "totalSale",
    "totalPurchase",
    "cashSale",
    "creditBill",
    "otherSale",
    "saleCollection",
    "subtotal",
    "miscSale",
    "miscPurchase",
    "transferOutCurr",
    "transferIn",
    "lessSaleReturn",
    "totalCashInHand",
    "oeAmt",
    "ordinaryAmt",
  ];
  const out = {};
  for (const k of keys) {
    out[k] = sumYearlyNumeric(rows, k);
  }
  return out;
}

/**
 * Load FY months one-by-one and report progress (for progress UI).
 * @param {number} fyStartYear FY start calendar year (April).
 * @param {(p: { loaded: number, total: number, rows: Array }) => void} [onProgress]
 */
export async function loadYearlySaleReportRowsWithProgress(
  fyStartYear,
  branchFilter = "Main",
  onProgress
) {
  const seq = getFinancialYearMonthSequence(fyStartYear);
  const rows = [];
  let loaded = 0;
  for (const spec of seq) {
    const data = await loadMonthSalePageData(spec.calendarYear, spec.monthIndex, branchFilter);
    loaded += 1;
    rows.push({
      ...spec,
      data,
      monthLabel: formatMonthShortYear(spec.calendarYear, spec.monthIndex),
    });
    if (typeof onProgress === "function") {
      onProgress({ loaded, total: 12, rows: rows.map((r) => ({ ...r })) });
    }
  }
  return rows;
}

/** Per-brand current-month sale (column `c`) by FY slot (Apr=0 … Mar=11) + YTD. */
export function buildBrandYearlyMatrix(rows) {
  const matrix = Object.fromEntries(MONTHLY_AUTOS_BRANDS.map((b) => [b, Array(12).fill(0)]));
  for (const { fySlot, data } of rows) {
    if (fySlot == null || fySlot < 0 || fySlot > 11) continue;
    const list = data?.brands || [];
    for (const row of list) {
      const n = row?.n;
      if (n != null && matrix[n]) {
        matrix[n][fySlot] = Number(row.c) || 0;
      }
    }
  }
  return MONTHLY_AUTOS_BRANDS.map((brand) => ({
    brand,
    monthly: matrix[brand],
    ytd: matrix[brand].reduce((a, x) => a + x, 0),
  }));
}

export function pctYoY(current, prior) {
  const c = Number(current) || 0;
  const p = Number(prior) || 0;
  if (!p) return c ? 100 : 0;
  return ((c - p) / Math.abs(p)) * 100;
}

/**
 * CSV (Excel-friendly) for the main yearly grid + YTD row.
 */
export function buildYearlyGridCsv(rows, totals, fyStartYear, branchLabel) {
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const headers = [
    "FY",
    "Branch",
    "Month",
    "Total Sale",
    "Total Purchase",
    "Cash Sale",
    "Credit Bill",
    "Other Sale",
    "Sale Collection",
    "Subtotal",
    "Misc Sale",
    "Misc Purchase",
    "Branch Transfer Out",
  ];
  const esc = (v) => {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(",")];
  for (const { monthLabel, data } of rows) {
    const m = [
      fyLabel,
      branchLabel,
      monthLabel,
      data?.totalSale ?? "",
      data?.totalPurchase ?? "",
      data?.cashSale ?? "",
      data?.creditBill ?? "",
      data?.otherSale ?? "",
      data?.saleCollection ?? "",
      data?.subtotal ?? "",
      data?.miscSale ?? "",
      data?.miscPurchase ?? "",
      data?.transferOutCurr ?? "",
    ].map(esc);
    lines.push(m.join(","));
  }
  const y = [
    fyLabel,
    branchLabel,
    "YTD",
    totals.totalSale ?? "",
    totals.totalPurchase ?? "",
    totals.cashSale ?? "",
    totals.creditBill ?? "",
    totals.otherSale ?? "",
    totals.saleCollection ?? "",
    totals.subtotal ?? "",
    totals.miscSale ?? "",
    totals.miscPurchase ?? "",
    totals.transferOutCurr ?? "",
  ].map(esc);
  lines.push(y.join(","));
  return lines.join("\n");
}

export function buildBrandMatrixCsv(brandRows, fyStartYear, branchLabel) {
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const headers = ["FY", "Branch", "Brand", ...FY_MONTH_ORDER_SHORT, "YTD"];
  const esc = (v) => {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(",")];
  for (const { brand, monthly, ytd } of brandRows) {
    lines.push(
      [esc(fyLabel), esc(branchLabel), esc(brand), ...monthly.map((x) => esc(x)), esc(ytd)].join(",")
    );
  }
  return lines.join("\n");
}
